import { useCallback, useRef, useState } from 'react';
import { tenantApi } from '@/modules/tenant/services/tenantApi';
import { docTypeHeuristic, type DocType } from '../docTypeHeuristic';
import type { PendingBoatImage } from '@/modules/inventory/services/boatMediaApi';

/**
 * DOC-02 — the combined smart-upload hook (the public/private SAFETY GATE,
 * client half). A single dropzone auto-splits dropped files by MIME — images
 * to the public Boat Images bucket, PDF/Office to the private Boat Documents
 * bucket — and the dealer can re-route any item across that boundary before
 * commit. commit() POSTs the confirmed batch to the server-side endpoint
 * (45-06 Task 1), which is authoritative on the final routing.
 *
 * Uses a concurrency-capped pool with a stable `clientId` key that never
 * changes on settle, and carries each item's FINAL bucket + suggestions to one
 * combined endpoint.
 */

export type UploadBucket = 'image' | 'document';

/** Image MIME prefixes auto-split to the public bucket. */
const IMAGE_MIME = /^image\//;

/** PDF + the common Office MIME types auto-split to the private bucket. */
const DOCUMENT_MIME = new Set<string>([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
]);

/** Concurrency cap for the local staging pool. */
const MAX_CONCURRENT_UPLOADS = 4;

export interface CombinedUploadItem {
  /**
   * Stable client-side identifier set once at drop (`pending-N`). It is the
   * React key, the re-route target, and the merge key — and it does NOT
   * change when the item settles or is re-routed.
   */
  clientId: string;
  file: File;
  fileName: string;
  size: number;
  mimeType: string;
  /** Local blob URL for an immediate preview before any server response. */
  localPreview: string;
  /** The item's CURRENT bucket — flips on re-route, sent to the server on commit. */
  bucket: UploadBucket;
  /** Document-bucket suggestion (seeded by the filename heuristic, overridable). */
  docType?: DocType;
  /**
   * Image-bucket suggestion filled ASYNC by the 45-04 classifier. A pending
   * angle (undefined) must NEVER block commit (UI-SPEC: Confirm not gated on AI).
   */
  seoAngle?: string;
  /** True while the angle classifier is still running for this image. */
  angleComputing?: boolean;
  /** Local staging state. */
  staging: boolean;
  progress: number;
  error?: string;
}

/** Auto-split a freshly-dropped file's MIME into its default bucket. */
export function bucketForMime(mimeType: string): UploadBucket {
  if (IMAGE_MIME.test(mimeType)) {
    return 'image';
  }
  if (DOCUMENT_MIME.has(mimeType)) {
    return 'document';
  }
  // Unknown MIME (e.g. an octet-stream Office file) defaults to the private
  // bucket — the safe default keeps an ambiguous file out of the public
  // storefront until the dealer re-routes it.
  return 'document';
}

/**
 * A committed image as returned by InventoryCombinedUploadController::formatImages.
 * NOTE the deliberate gap vs the /media endpoint's InventoryMediaItem: the
 * combined-upload response has NO `conversions` map (only the original `url`),
 * so a consumer seeding the boat-media cache from this result must tolerate an
 * absent `conversions` (mapQueryImageToCommittedCard already falls back to
 * `url`). seo_angle/order mirror the controller payload.
 */
export interface CombinedUploadImage {
  id: number;
  file_name: string;
  mime_type?: string | null;
  size?: number | null;
  url: string;
  seo_angle?: string | null;
  order?: number | null;
}

export interface CombinedUploadResult {
  images: CombinedUploadImage[];
  /**
   * #1219 — a boat canvas commit's image files, held private until the classifier
   * routes them (so NOT in `images` yet). Empty for explicit uploads and assets.
   */
  pending_images?: PendingBoatImage[];
  documents: unknown[];
}

/**
 * `basePath` — SP3-full slice 1: the combined-upload endpoint is item-type
 * generic on the server; pass '/engines' or '/trailers' to bind the hook to
 * an asset instead of a boat. Defaults to '/boats' (every existing caller).
 *
 * `suppressExtract` — #581 Task 9: the canvas flows (LiveFillCanvas /
 * CanvasHeroIngest, AssetCanvas) drive extraction themselves via
 * useFactoryPacketExtract / useAssetExtractFill, so their commits must
 * suppress the backend's auto-trigger propose-mode extraction (#581 Task 5)
 * to avoid dispatching a duplicate/competing extraction job. Defaults to
 * false so every pre-existing caller (DocumentsTab, BoatDetailMainTabs) keeps
 * the server's auto-trigger-on default unchanged.
 */
export function useCombinedUpload(
  boatId: string | null,
  basePath = '/boats',
  suppressExtract = false,
) {
  const [items, setItems] = useState<CombinedUploadItem[]>([]);
  const [committing, setCommitting] = useState(false);
  const [commitError, setCommitError] = useState<string | undefined>(undefined);
  const counterRef = useRef(0);
  const queueRef = useRef<string[]>([]);
  const inFlightRef = useRef(0);

  const updateItem = useCallback(
    (clientId: string, patch: Partial<CombinedUploadItem>) => {
      setItems((prev) =>
        prev.map((item) =>
          item.clientId === clientId ? { ...item, ...patch } : item,
        ),
      );
    },
    [],
  );

  // Local staging "pool" — uses a drain/runOne shape so
  // the review screen can show per-item readiness. The combined upload does
  // not promote to a temp collection (the server commits the bytes directly
  // on confirm), so staging here just flips the item to ready in a
  // concurrency-capped way, keeping the UI idiom identical to the wizard.
  const runOne = useCallback(
    (clientId: string) => {
      updateItem(clientId, { staging: false, progress: 100 });
    },
    [updateItem],
  );

  const drain = useCallback(() => {
    while (
      inFlightRef.current < MAX_CONCURRENT_UPLOADS &&
      queueRef.current.length > 0
    ) {
      const clientId = queueRef.current.shift()!;
      inFlightRef.current += 1;
      runOne(clientId);
      inFlightRef.current -= 1;
    }
  }, [runOne]);

  /**
   * Auto-split dropped files by MIME into their default buckets. An image is
   * still re-routable to document afterwards (and vice-versa).
   *
   * `presetDocType` — optional. When the upload was started from a typed entry
   * point ("Upload invoice" / "Upload certificate of origin"), the caller
   * passes the intended doc_type so every freshly-staged DOCUMENT item is
   * pre-filled with it (overriding the filename heuristic). The dealer can
   * still change it per item on the review screen; image items are unaffected.
   *
   * RETURNS the freshly-staged items it just created. Callers that commit in
   * the SAME tick as the drop (e.g. the inline canvas hero) cannot read the
   * post-setItems `items` state yet — setItems is async — so they pass this
   * return value straight into `commit({ items })` to avoid committing the
   * pre-drop (empty) snapshot. Existing callers may safely ignore it.
   */
  const onDrop = useCallback(
    (files: File[], presetDocType?: DocType): CombinedUploadItem[] => {
      const newItems: CombinedUploadItem[] = [];
      const enqueue: string[] = [];

      for (const file of files) {
        const clientId = `pending-${++counterRef.current}`;
        const bucket = bucketForMime(file.type);
        newItems.push({
          clientId,
          file,
          fileName: file.name,
          size: file.size,
          mimeType: file.type,
          localPreview: URL.createObjectURL(file),
          bucket,
          // Seed the document suggestion — a caller-supplied preset wins over
          // the filename heuristic. Image items have no doc_type; their
          // seo_angle arrives async.
          docType:
            bucket === 'document'
              ? (presetDocType ?? docTypeHeuristic(file.name))
              : undefined,
          angleComputing: bucket === 'image',
          staging: true,
          progress: 0,
        });
        enqueue.push(clientId);
      }

      setItems((prev) => [...prev, ...newItems]);
      queueRef.current.push(...enqueue);
      drain();
      return newItems;
    },
    [drain],
  );

  /** Flip an item's bucket (Public ↔ Private). Reversible any number of times. */
  const reroute = useCallback((clientId: string) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.clientId !== clientId) {
          return item;
        }
        const nextBucket: UploadBucket =
          item.bucket === 'image' ? 'document' : 'image';
        return {
          ...item,
          bucket: nextBucket,
          // Re-routing INTO documents seeds a doc_type guess if none yet; the
          // angle suggestion only ever applies to the image bucket.
          docType:
            nextBucket === 'document'
              ? (item.docType ?? docTypeHeuristic(item.fileName))
              : undefined,
          angleComputing: nextBucket === 'image' ? item.angleComputing : false,
        };
      }),
    );
  }, []);

  /** Override a document item's doc_type (the inline Select on the doc card). */
  const setDocType = useCallback((clientId: string, docType: DocType) => {
    setItems((prev) =>
      prev.map((item) =>
        item.clientId === clientId ? { ...item, docType } : item,
      ),
    );
  }, []);

  /** Set/clear an image item's seo_angle (the AI classifier callback OR a manual edit). */
  const setSeoAngle = useCallback(
    (clientId: string, seoAngle: string | undefined, computing = false) => {
      setItems((prev) =>
        prev.map((item) =>
          item.clientId === clientId
            ? { ...item, seoAngle, angleComputing: computing }
            : item,
        ),
      );
    },
    [],
  );

  /** Discard a single staged item before commit. */
  const remove = useCallback((clientId: string) => {
    setItems((prev) => {
      const item = prev.find((i) => i.clientId === clientId);
      if (item?.localPreview) {
        URL.revokeObjectURL(item.localPreview);
      }
      return prev.filter((i) => i.clientId !== clientId);
    });
  }, []);

  /** Discard the whole staged batch (Discard upload). */
  const clear = useCallback(() => {
    setItems((prev) => {
      for (const item of prev) {
        if (item.localPreview) {
          URL.revokeObjectURL(item.localPreview);
        }
      }
      return [];
    });
    queueRef.current = [];
    setCommitError(undefined);
  }, []);

  /**
   * Build the combined multipart payload (files + a parallel items[] array,
   * each carrying its FINAL bucket + suggestion) and POST it to the
   * server-side combined-upload endpoint. The server is authoritative — it
   * re-enforces the per-item bucket regardless of what the client sends.
   *
   * commit() is NEVER blocked on a pending seo_angle: an image whose angle is
   * still computing commits with no seo_angle (the dealer edits it later).
   *
   * `opts` — optional, fully backward-compatible. `commit()` with no args
   * behaves exactly as before (posts the current `items` to the hook's bound
   * `boatId`). A caller that stages + commits in the SAME render tick — where
   * neither setItems nor a freshly-bound boatId have flushed yet — passes the
   * just-dropped batch and/or the just-created boat id explicitly:
   *   commit({ boatId: freshId, items: staged })
   * `opts.items` overrides the (stale) `items` snapshot; `opts.boatId`
   * overrides the (stale) bound `boatId` for BOTH the required-id guard and
   * the POST URL.
   */
  const commit = useCallback(async (
    opts?: { boatId?: string; items?: CombinedUploadItem[] },
  ): Promise<CombinedUploadResult> => {
    const targetBoatId = opts?.boatId ?? boatId;
    if (!targetBoatId) {
      throw new Error('boatId required — cannot commit a combined upload without a saved boat.');
    }

    const snapshot = opts?.items ?? items;
    const formData = new FormData();

    snapshot.forEach((item, index) => {
      formData.append('files[]', item.file);
      formData.append(`items[${index}][bucket]`, item.bucket);
      if (item.bucket === 'document') {
        if (item.docType) {
          formData.append(`items[${index}][doc_type]`, item.docType);
        }
      } else if (item.seoAngle) {
        // Only sent when present — a pending angle is simply omitted, never
        // gating the commit.
        formData.append(`items[${index}][seo_angle]`, item.seoAngle);
      }
    });

    // #581 Task 9 — suppress the backend's auto-trigger extraction ONLY when
    // requested; omitted entirely (not '0') when false so the server default
    // (auto-trigger on) is untouched. MUST be the string '1': Laravel's
    // boolean validator accepts '0'/'1' but NOT 'true'/'false' string tokens
    // (precedent: remove_logo: '1' in useDealershipSettings.ts).
    if (suppressExtract) {
      formData.append('suppress_extract', '1');
    }

    setCommitting(true);
    setCommitError(undefined);
    try {
      const result = await tenantApi.postOne<CombinedUploadResult>(
        `${basePath}/${targetBoatId}/combined-upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );

      // BUG FIX (staged↔committed duplication): the server commits the WHOLE
      // snapshot atomically, so every item posted here is now a real committed
      // row. The canvas renders committed media from the useBoatMedia /
      // useDocuments queries (clientId `existing-*` / the doc id) AND staged
      // items from this hook's `items` (clientId `pending-*`). Those namespaces
      // are disjoint, so leaving the staged items in place after a successful
      // commit makes every freshly-dropped file render TWICE for the rest of the
      // session (a staged card + a committed card). Clear the committed items
      // here, keyed by the committed SNAPSHOT's clientIds (NOT setItems([])): a
      // setItems([]) would also wipe any batch-B items a dealer staged while
      // batch-A's commit was in flight, but those were never posted. Filtering by
      // the snapshot's clientIds removes exactly the committed batch and leaves
      // any concurrently-staged items intact.
      //
      // Blob revoke is safe: a committed card reads the server `url` /
      // `download_url`, never `localPreview`, and the staged card unmounts in the
      // SAME setItems pass that revokes its blob — so no live <img> ever points
      // at a revoked object URL.
      const committedIds = new Set(snapshot.map((i) => i.clientId));
      setItems((prev) => {
        for (const it of prev) {
          if (committedIds.has(it.clientId) && it.localPreview) {
            URL.revokeObjectURL(it.localPreview);
          }
        }
        return prev.filter((it) => !committedIds.has(it.clientId));
      });

      return result;
    } catch (error) {
      // Non-destructive on failure: keep `items` + commitError so the inline
      // error UI can render and the dealer can retry the same staged batch.
      setCommitError('Upload failed');
      throw error;
    } finally {
      setCommitting(false);
    }
  }, [boatId, basePath, items, suppressExtract]);

  const imageItems = items.filter((item) => item.bucket === 'image');
  const documentItems = items.filter((item) => item.bucket === 'document');

  return {
    items,
    imageItems,
    documentItems,
    onDrop,
    reroute,
    setDocType,
    setSeoAngle,
    remove,
    clear,
    commit,
    committing,
    commitError,
    isStaging: items.some((item) => item.staging),
    isEmpty: items.length === 0,
  };
}
